import { useEffect, useState, useRef } from 'react'
import {
  Cog6ToothIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BellIcon,
  PaperAirplaneIcon,
  SwatchIcon,
  PhotoIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { Card, InlineSpinner, Button } from '@/components/ui'
import apiClient from '@/api/client'
import { themeApi, ThemeSettings } from '@/api/theme'
import { useTheme } from '@/contexts/ThemeContext'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'

interface EmailSettings {
  smtp_server: string
  smtp_port: string
  smtp_protocol: string
  smtp_user: string
  smtp_password: string
  smtp_from_email: string
  smtp_from_name: string
}

const defaultEmailSettings: EmailSettings = {
  smtp_server: '',
  smtp_port: '587',
  smtp_protocol: 'TLS',
  smtp_user: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: 'Community Platform',
}

// Default theme settings matching current appearance
const defaultThemeSettings: ThemeSettings = {
  theme_company_name: 'CONQUIST',
  theme_app_name: 'GrowUp!',
  theme_tagline: 'Community Platform',
  theme_login_message: 'Inserisci le tue credenziali per accedere',
  theme_copyright: 'Conquist s.r.l.',
  theme_logo_url: '',
  theme_favicon_url: '',
  theme_primary_color: '#f97316',
  theme_secondary_color: '#ea580c',
  theme_accent_color: '#dc2626',
  theme_sidebar_bg: '#475569',
  theme_sidebar_text: '#ffffff',
  theme_company_color_sidebar: '#7dd3fc',
  theme_page_bg: '#f1f5f9',
  theme_card_bg: '#ffffff',
  theme_card_text: '#111827',
  theme_login_bg: '#f1f5f9',
  theme_login_card_bg: '#ffffff',
  theme_login_text: '#111827',
  theme_company_color_login: '#0f172a',
}

// Color picker component
interface ColorPickerProps {
  label: string
  value: string
  onChange: (value: string) => void
  description?: string
}

function ColorPicker({ label, value, onChange, description }: ColorPickerProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
          placeholder="#000000"
        />
      </div>
      {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
    </div>
  )
}

type TabType = 'email' | 'general' | 'security' | 'notifications' | 'theme'

export default function AdminSettingsPage() {
  const { user } = useAuthStore()
  const { refreshTheme } = useTheme()
  const [activeTab, setActiveTab] = useState<TabType>('email')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testingEmail, setTestingEmail] = useState(false)
  const [emailSettings, setEmailSettings] = useState<EmailSettings>(defaultEmailSettings)
  const [testEmailAddress, setTestEmailAddress] = useState('')

  // Theme state
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(defaultThemeSettings)
  const [themeLoading, setThemeLoading] = useState(false)
  const [themeSaving, setThemeSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  // Only super_admin can access this page
  const isSuperAdmin = user?.role === 'super_admin'

  useEffect(() => {
    if (isSuperAdmin) {
      fetchEmailSettings()
    }
  }, [isSuperAdmin])

  // Fetch theme settings when theme tab is active
  useEffect(() => {
    if (activeTab === 'theme' && isSuperAdmin) {
      fetchThemeSettings()
    }
  }, [activeTab, isSuperAdmin])

  const fetchEmailSettings = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/settings/email/')
      const data = response.data
      setEmailSettings({
        smtp_server: data.smtp_server?.value || '',
        smtp_port: data.smtp_port?.value || '587',
        smtp_protocol: data.smtp_protocol?.value || 'TLS',
        smtp_user: data.smtp_user?.value || '',
        smtp_password: data.smtp_password?.value === '********' ? '' : (data.smtp_password?.value || ''),
        smtp_from_email: data.smtp_from_email?.value || '',
        smtp_from_name: data.smtp_from_name?.value || 'Community Platform',
      })
    } catch (error) {
      console.error('Error fetching email settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveEmailSettings = async () => {
    setSaving(true)
    try {
      const payload: Record<string, string> = {
        smtp_server: emailSettings.smtp_server,
        smtp_port: emailSettings.smtp_port,
        smtp_protocol: emailSettings.smtp_protocol,
        smtp_user: emailSettings.smtp_user,
        smtp_from_email: emailSettings.smtp_from_email,
        smtp_from_name: emailSettings.smtp_from_name,
      }

      // Only include password if it's been changed (not empty)
      if (emailSettings.smtp_password) {
        payload.smtp_password = emailSettings.smtp_password
      }

      await apiClient.post('/admin/settings/email/', payload)
      toast.success('Impostazioni email salvate con successo')
    } catch (error) {
      console.error('Error saving email settings:', error)
      toast.error('Errore nel salvataggio delle impostazioni')
    } finally {
      setSaving(false)
    }
  }

  const handleTestEmail = async () => {
    if (!testEmailAddress && !user?.email) {
      toast.error('Inserisci un indirizzo email per il test')
      return
    }

    setTestingEmail(true)
    try {
      const response = await apiClient.post('/admin/settings/test_email/', {
        to_email: testEmailAddress || user?.email,
      })
      toast.success(response.data.message || 'Email di test inviata con successo!')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Errore nell\'invio dell\'email di test'
      toast.error(message)
    } finally {
      setTestingEmail(false)
    }
  }

  // Theme functions
  const fetchThemeSettings = async () => {
    setThemeLoading(true)
    try {
      const data = await themeApi.getSettings()
      setThemeSettings({
        ...defaultThemeSettings,
        ...data,
      })
    } catch (error) {
      console.error('Error fetching theme settings:', error)
      // Use defaults on error
      setThemeSettings(defaultThemeSettings)
    } finally {
      setThemeLoading(false)
    }
  }

  const handleSaveThemeSettings = async () => {
    setThemeSaving(true)
    try {
      await themeApi.updateSettings(themeSettings)
      await refreshTheme()
      toast.success('Tema salvato con successo! Le modifiche sono visibili immediatamente.')
    } catch (error: any) {
      console.error('Error saving theme settings:', error)
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del tema')
    } finally {
      setThemeSaving(false)
    }
  }

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Il file deve essere un\'immagine (PNG, JPG, SVG)')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Il file non può superare i 2MB')
      return
    }

    setUploadingLogo(true)
    try {
      const response = await themeApi.uploadLogo(file)
      setThemeSettings({ ...themeSettings, theme_logo_url: response.logo_url })
      await refreshTheme()
      toast.success('Logo caricato con successo!')
    } catch (error: any) {
      console.error('Error uploading logo:', error)
      toast.error(error.response?.data?.message || 'Errore nel caricamento del logo')
    } finally {
      setUploadingLogo(false)
      // Reset input
      if (logoInputRef.current) {
        logoInputRef.current.value = ''
      }
    }
  }

  const handleRemoveLogo = async () => {
    try {
      await themeApi.removeLogo()
      setThemeSettings({ ...themeSettings, theme_logo_url: '' })
      await refreshTheme()
      toast.success('Logo rimosso')
    } catch (error: any) {
      console.error('Error removing logo:', error)
      toast.error(error.response?.data?.message || 'Errore nella rimozione del logo')
    }
  }

  const handleResetTheme = () => {
    setThemeSettings(defaultThemeSettings)
    toast('Valori ripristinati ai default. Clicca "Salva" per applicare.')
  }

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">Accesso non autorizzato</h3>
        <p className="mt-1 text-gray-500">
          Solo gli utenti Super Admin possono accedere alle impostazioni di sistema.
        </p>
      </div>
    )
  }

  const tabs = [
    { id: 'email' as TabType, name: 'Email / SMTP', icon: EnvelopeIcon },
    { id: 'theme' as TabType, name: 'Tema', icon: SwatchIcon },
    // TODO: Riabilitare dopo la demo
    // { id: 'general' as TabType, name: 'Generali', icon: Cog6ToothIcon },
    // { id: 'security' as TabType, name: 'Sicurezza', icon: ShieldCheckIcon },
    // { id: 'notifications' as TabType, name: 'Notifiche', icon: BellIcon },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Impostazioni Sistema</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configura le impostazioni globali della piattaforma
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <InlineSpinner />
        </div>
      ) : (
        <>
          {/* Email / SMTP Settings */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Configurazione SMTP
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Configura il server SMTP per l'invio delle email (notifiche, password reset, newsletter)
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Server SMTP */}
                    <div>
                      <label htmlFor="smtp_server" className="block text-sm font-medium text-gray-700">
                        Server SMTP
                      </label>
                      <input
                        id="smtp_server"
                        type="text"
                        value={emailSettings.smtp_server}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="smtp.example.com"
                      />
                    </div>

                    {/* Porta SMTP */}
                    <div>
                      <label htmlFor="smtp_port" className="block text-sm font-medium text-gray-700">
                        Porta
                      </label>
                      <input
                        id="smtp_port"
                        type="text"
                        value={emailSettings.smtp_port}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="587"
                      />
                    </div>

                    {/* Protocollo */}
                    <div>
                      <label htmlFor="smtp_protocol" className="block text-sm font-medium text-gray-700">
                        Protocollo
                      </label>
                      <select
                        id="smtp_protocol"
                        value={emailSettings.smtp_protocol}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_protocol: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="TLS">TLS</option>
                        <option value="SSL">SSL</option>
                        <option value="NONE">Nessuno</option>
                      </select>
                    </div>

                    {/* Username */}
                    <div>
                      <label htmlFor="smtp_user" className="block text-sm font-medium text-gray-700">
                        Username
                      </label>
                      <input
                        id="smtp_user"
                        type="text"
                        value={emailSettings.smtp_user}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="user@example.com"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="smtp_password" className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <input
                        id="smtp_password"
                        type="password"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="********"
                      />
                      <p className="mt-1 text-xs text-gray-500">Lascia vuoto per mantenere la password esistente</p>
                    </div>

                    {/* Email mittente */}
                    <div>
                      <label htmlFor="smtp_from_email" className="block text-sm font-medium text-gray-700">
                        Email mittente
                      </label>
                      <input
                        id="smtp_from_email"
                        type="email"
                        value={emailSettings.smtp_from_email}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_email: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="noreply@example.com"
                      />
                    </div>

                    {/* Nome mittente */}
                    <div className="md:col-span-2">
                      <label htmlFor="smtp_from_name" className="block text-sm font-medium text-gray-700">
                        Nome mittente
                      </label>
                      <input
                        id="smtp_from_name"
                        type="text"
                        value={emailSettings.smtp_from_name}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_name: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Community Platform"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button onClick={handleSaveEmailSettings} disabled={saving}>
                      {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Test Email */}
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Test Configurazione
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Invia un'email di test per verificare che la configurazione SMTP sia corretta.
                  </p>

                  <div className="flex items-end gap-4">
                    <div className="flex-1">
                      <label htmlFor="test_email" className="block text-sm font-medium text-gray-700">
                        Email destinatario
                      </label>
                      <input
                        id="test_email"
                        type="email"
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder={user?.email || 'test@example.com'}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      onClick={handleTestEmail}
                      disabled={testingEmail}
                      className="flex items-center gap-2"
                    >
                      {testingEmail ? (
                        <InlineSpinner className="h-4 w-4" />
                      ) : (
                        <PaperAirplaneIcon className="h-4 w-4" />
                      )}
                      {testingEmail ? 'Invio...' : 'Invia Email di Test'}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Theme Settings */}
          {activeTab === 'theme' && (
            <div className="space-y-6">
              {themeLoading ? (
                <div className="flex justify-center py-12">
                  <InlineSpinner />
                </div>
              ) : (
                <>
                  {/* Branding & Testi */}
                  <Card>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">Branding e Testi</h3>
                          <p className="text-sm text-gray-500">Personalizza nome, slogan e messaggi della piattaforma</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Nome Azienda</label>
                          <input
                            type="text"
                            value={themeSettings.theme_company_name}
                            onChange={(e) => setThemeSettings({ ...themeSettings, theme_company_name: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="CONQUIST"
                          />
                          <p className="mt-1 text-xs text-gray-500">Mostrato nella sidebar accanto al logo</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Nome App</label>
                          <input
                            type="text"
                            value={themeSettings.theme_app_name}
                            onChange={(e) => setThemeSettings({ ...themeSettings, theme_app_name: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="GrowUp!"
                          />
                          <p className="mt-1 text-xs text-gray-500">Mostrato nel titolo e nella pagina login</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Tagline / Payoff</label>
                          <input
                            type="text"
                            value={themeSettings.theme_tagline}
                            onChange={(e) => setThemeSettings({ ...themeSettings, theme_tagline: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Community Platform"
                          />
                          <p className="mt-1 text-xs text-gray-500">Sottotitolo nella sidebar e login</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Copyright</label>
                          <input
                            type="text"
                            value={themeSettings.theme_copyright}
                            onChange={(e) => setThemeSettings({ ...themeSettings, theme_copyright: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Conquist s.r.l."
                          />
                          <p className="mt-1 text-xs text-gray-500">Testo del copyright nel footer login</p>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Messaggio Login</label>
                          <input
                            type="text"
                            value={themeSettings.theme_login_message}
                            onChange={(e) => setThemeSettings({ ...themeSettings, theme_login_message: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Inserisci le tue credenziali per accedere"
                          />
                          <p className="mt-1 text-xs text-gray-500">Messaggio mostrato sopra il form di login</p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Logo Upload */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Logo</h3>
                      <p className="text-sm text-gray-500 mb-4">Carica un logo personalizzato (PNG, JPG o SVG, max 2MB)</p>

                      <div className="flex items-center gap-6">
                        {/* Logo Preview */}
                        <div
                          className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden"
                          style={{ backgroundColor: themeSettings.theme_sidebar_bg }}
                        >
                          {themeSettings.theme_logo_url ? (
                            <img
                              src={themeSettings.theme_logo_url}
                              alt="Logo"
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <PhotoIcon className="h-10 w-10 text-gray-400" />
                          )}
                        </div>

                        <div className="flex-1">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <div className="flex gap-3">
                            <Button
                              variant="secondary"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={uploadingLogo}
                              className="flex items-center gap-2"
                            >
                              {uploadingLogo ? (
                                <InlineSpinner className="h-4 w-4" />
                              ) : (
                                <PhotoIcon className="h-4 w-4" />
                              )}
                              {uploadingLogo ? 'Caricamento...' : 'Carica Logo'}
                            </Button>
                            {themeSettings.theme_logo_url && (
                              <Button
                                variant="secondary"
                                onClick={handleRemoveLogo}
                                className="flex items-center gap-2 text-red-600 hover:text-red-700"
                              >
                                <TrashIcon className="h-4 w-4" />
                                Rimuovi
                              </Button>
                            )}
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Se non viene caricato un logo, verrà usato il logo predefinito SVG
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Colori Principali */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Colori Principali</h3>
                      <p className="text-sm text-gray-500 mb-4">Colori per bottoni, link e elementi interattivi</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <ColorPicker
                          label="Colore Primario"
                          value={themeSettings.theme_primary_color}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_primary_color: value })}
                          description="Bottoni, link, elementi attivi"
                        />
                        <ColorPicker
                          label="Colore Secondario"
                          value={themeSettings.theme_secondary_color}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_secondary_color: value })}
                          description="Hover bottoni, varianti"
                        />
                        <ColorPicker
                          label="Colore Accent"
                          value={themeSettings.theme_accent_color}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_accent_color: value })}
                          description="Badge, alert, evidenziazioni"
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Sidebar & Header */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Sidebar e Header</h3>
                      <p className="text-sm text-gray-500 mb-4">Colori del menu laterale e della barra superiore</p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <ColorPicker
                          label="Sfondo Sidebar/Header"
                          value={themeSettings.theme_sidebar_bg}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_sidebar_bg: value })}
                          description="Colore di sfondo del menu laterale"
                        />
                        <ColorPicker
                          label="Testo Sidebar/Header"
                          value={themeSettings.theme_sidebar_text}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_sidebar_text: value })}
                          description="Colore del testo e icone nel menu"
                        />
                        <ColorPicker
                          label="Colore Nome Azienda"
                          value={themeSettings.theme_company_color_sidebar}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_company_color_sidebar: value })}
                          description="Colore del nome azienda nella sidebar"
                        />
                      </div>

                      {/* Preview */}
                      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: themeSettings.theme_sidebar_bg }}>
                        <p className="text-sm font-medium">
                          <span style={{ color: themeSettings.theme_company_color_sidebar }}>{themeSettings.theme_company_name}</span>{' '}
                          <span style={{ color: themeSettings.theme_sidebar_text }}>{themeSettings.theme_app_name}</span>
                        </p>
                        <p className="text-xs mt-1" style={{ color: themeSettings.theme_sidebar_text, opacity: 0.7 }}>
                          {themeSettings.theme_tagline}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Page Background & Cards */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Pagine e Schede</h3>
                      <p className="text-sm text-gray-500 mb-4">Colori di sfondo delle pagine e delle card</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ColorPicker
                          label="Sfondo Pagine"
                          value={themeSettings.theme_page_bg}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_page_bg: value })}
                          description="Colore di sfondo generale delle pagine"
                        />
                        <ColorPicker
                          label="Sfondo Card"
                          value={themeSettings.theme_card_bg}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_card_bg: value })}
                          description="Colore di sfondo delle schede"
                        />
                        <ColorPicker
                          label="Testo Card"
                          value={themeSettings.theme_card_text}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_card_text: value })}
                          description="Colore del testo nelle schede"
                        />
                      </div>

                      {/* Preview */}
                      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: themeSettings.theme_page_bg }}>
                        <div className="p-4 rounded shadow" style={{ backgroundColor: themeSettings.theme_card_bg }}>
                          <p className="font-medium" style={{ color: themeSettings.theme_card_text }}>
                            Anteprima Card
                          </p>
                          <p className="text-sm mt-1" style={{ color: themeSettings.theme_card_text, opacity: 0.7 }}>
                            Questo è un esempio di come appariranno le schede
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Login Page */}
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Pagina Login</h3>
                      <p className="text-sm text-gray-500 mb-4">Colori specifici per la pagina di accesso</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ColorPicker
                          label="Sfondo Login"
                          value={themeSettings.theme_login_bg}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_login_bg: value })}
                          description="Sfondo della pagina login"
                        />
                        <ColorPicker
                          label="Sfondo Form Login"
                          value={themeSettings.theme_login_card_bg}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_login_card_bg: value })}
                          description="Sfondo del box con il form"
                        />
                        <ColorPicker
                          label="Testo Login"
                          value={themeSettings.theme_login_text}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_login_text: value })}
                          description="Colore del testo nella pagina"
                        />
                        <ColorPicker
                          label="Colore Nome Azienda (Footer)"
                          value={themeSettings.theme_company_color_login}
                          onChange={(value) => setThemeSettings({ ...themeSettings, theme_company_color_login: value })}
                          description="Colore del nome azienda nel footer login"
                        />
                      </div>

                      {/* Preview */}
                      <div className="mt-6 p-6 rounded-lg" style={{ backgroundColor: themeSettings.theme_login_bg }}>
                        <div className="max-w-sm mx-auto p-4 rounded shadow" style={{ backgroundColor: themeSettings.theme_login_card_bg }}>
                          <p className="text-lg font-bold text-center" style={{ color: themeSettings.theme_login_text }}>
                            {themeSettings.theme_app_name}
                          </p>
                          <p className="text-sm text-center mt-1" style={{ color: themeSettings.theme_login_text, opacity: 0.7 }}>
                            {themeSettings.theme_login_message}
                          </p>
                          <div className="mt-4 space-y-3">
                            <div className="h-10 rounded" style={{ backgroundColor: themeSettings.theme_login_bg }} />
                            <div className="h-10 rounded" style={{ backgroundColor: themeSettings.theme_login_bg }} />
                            <div
                              className="h-10 rounded flex items-center justify-center text-sm font-medium"
                              style={{ backgroundColor: themeSettings.theme_primary_color, color: '#fff' }}
                            >
                              Accedi
                            </div>
                          </div>
                        </div>
                        <p className="text-center text-sm mt-4" style={{ color: themeSettings.theme_login_text, opacity: 0.6 }}>
                          &copy; 2025 {themeSettings.theme_app_name} by{' '}
                          <span className="font-bold" style={{ color: themeSettings.theme_company_color_login }}>
                            {themeSettings.theme_copyright}
                          </span>{' '}
                          Tutti i diritti riservati.
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="secondary"
                      onClick={handleResetTheme}
                      className="flex items-center gap-2"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Ripristina Default
                    </Button>

                    <Button
                      onClick={handleSaveThemeSettings}
                      disabled={themeSaving}
                    >
                      {themeSaving ? 'Salvataggio...' : 'Salva Tema'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* General Settings */}
          {activeTab === 'general' && (
            <Card>
              <div className="p-6 text-center text-gray-500">
                <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">Impostazioni Generali</h3>
                <p className="mt-1">Le impostazioni generali saranno disponibili prossimamente.</p>
              </div>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <Card>
              <div className="p-6 text-center text-gray-500">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">Impostazioni Sicurezza</h3>
                <p className="mt-1">Le impostazioni di sicurezza saranno disponibili prossimamente.</p>
              </div>
            </Card>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <Card>
              <div className="p-6 text-center text-gray-500">
                <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">Impostazioni Notifiche</h3>
                <p className="mt-1">Le impostazioni delle notifiche saranno disponibili prossimamente.</p>
              </div>
            </Card>
          )}

        </>
      )}
    </div>
  )
}
